import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

const LAST_UPDATED = "26 June 2026";

function Section({ n, title, children }) {
  return (
    <section className="mt-10 scroll-mt-24" id={`r-${n}`} data-testid={`refund-section-${n}`}>
      <h2 className="font-display text-2xl tracking-tight">
        <span className="text-neutral-400 mr-2">{n}.</span>{title}
      </h2>
      <div className="mt-3 space-y-3 text-sm text-neutral-700 leading-relaxed">{children}</div>
    </section>
  );
}

const SECTIONS = [
  "Overview", "Buyer Protection guarantee", "What's covered", "What's not covered",
  "How to request a refund", "How refunds are decided & issued", "Seller returns",
  "Fees & shipping", "Timeframes", "Contact us",
];

export default function Refunds() {
  return (
    <div data-testid="refunds-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="hairline-b pb-8">
        <div className="label-eyebrow">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Refund &amp; Returns Policy.</h1>
        <p className="mt-4 text-sm text-neutral-500 max-w-2xl">Last updated {LAST_UPDATED}</p>
        <p className="mt-4 text-base text-neutral-600 max-w-3xl leading-relaxed">
          Your Classic Style is a marketplace for individually-owned, mostly pre-owned items. This policy explains when
          and how you can get a refund, and works alongside our{" "}
          <Link to="/terms" className="underline">Terms &amp; Conditions</Link> and{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </div>

      <div className="mt-8 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="h-fit lg:sticky lg:top-24 hairline p-5" data-testid="refunds-toc">
          <div className="label-eyebrow">Contents</div>
          <ol className="mt-3 space-y-1.5 text-xs text-neutral-600">
            {SECTIONS.map((t, i) => (
              <li key={t}><a href={`#r-${i + 1}`} className="hover:text-black transition-colors">{i + 1}. {t}</a></li>
            ))}
          </ol>
        </aside>

        <div>
          <div className="hairline p-5 flex items-start gap-3" style={{ borderColor: "#2563EB" }} data-testid="refunds-disclaimer">
            <ShieldCheck size={16} style={{ color: "#2563EB" }} className="mt-0.5 shrink-0" />
            <p className="text-xs text-neutral-600 leading-relaxed">
              This is a plain-English template for your convenience and is not legal advice. Refund and consumer rights
              vary by location; please have a qualified lawyer confirm this policy for your region (for example US state
              law and, where relevant, EU/UK consumer-protection rules) before going live.
            </p>
          </div>

          <Section n={1} title="Overview">
            <p>
              Because items are sold by individuals, sellers are not required to offer change-of-mind returns. However,
              every eligible order is backed by <strong>Buyer Protection</strong>: if an item never arrives, is
              counterfeit, or is materially not as described, you can request a refund through the claim process below.
            </p>
          </Section>

          <Section n={2} title="Buyer Protection guarantee">
            <p>
              Buyer Protection covers eligible orders for <strong>3 days after delivery</strong> (the "guarantee
              window"). During this window the seller's payout is held. Buyer Protection is funded by the buyer
              protection fee shown at checkout.
            </p>
          </Section>

          <Section n={3} title="What's covered">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Item not received</strong> — it was never delivered and tracking does not show delivery.</li>
              <li><strong>Counterfeit</strong> — the item is a fake of the brand it was sold as.</li>
              <li><strong>Significantly not as described</strong> — wrong item, undisclosed major flaws or damage, wrong size versus the stated measurements, or missing parts that were not disclosed.</li>
            </ul>
          </Section>

          <Section n={4} title="What's not covered">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Change of mind</strong> — unless the seller separately offers returns.</li>
              <li><strong>Minor wear</strong> consistent with the disclosed condition grade and photos.</li>
              <li><strong>Fit issues</strong> where the listed measurements were accurate (please check measurements before buying).</li>
              <li>Issues reported <strong>after the guarantee window</strong> closes, or damage that occurs after delivery.</li>
              <li>Prohibited or hazardous items purchased against our rules.</li>
            </ul>
          </Section>

          <Section n={5} title="How to request a refund">
            <p>
              Go to <strong>Purchases</strong>, open the order, and start a claim within the guarantee window. Add clear
              photos and a short description of the problem. Filing a claim keeps the seller's payout on hold while we
              review. If your parcel simply hasn't arrived, contact us and we'll help trace it.
            </p>
          </Section>

          <Section n={6} title="How refunds are decided & issued">
            <p>
              Our team, aided by AI screening, reviews your evidence against the listing and the seller's authenticity
              proof, and the seller has a chance to respond. Where a claim is upheld, you are refunded and the seller's
              payout is reversed. Where the evidence is inconclusive, we generally resolve in the buyer's favour without
              penalising the seller. Refunds are issued to your <strong>original payment method</strong>. We may ask you
              to return the item (typically with a prepaid label) before a refund completes.
            </p>
          </Section>

          <Section n={7} title="Seller returns">
            <p>
              Sellers may choose to accept change-of-mind returns; if offered, the terms (window, who pays return
              shipping, and any restocking approach) are shown on the listing or arranged through support. Sale items
              that are accurately described are otherwise final unless covered by Buyer Protection above.
            </p>
          </Section>

          <Section n={8} title="Fees & shipping">
            <p>
              When a claim is upheld for a not-as-described, counterfeit, or non-delivered item, your refund includes the
              item price, the buyer protection fee, and the original shipping you paid. For seller-approved change-of-mind
              returns, the buyer protection fee and original/return shipping are generally non-refundable unless the
              seller agrees otherwise.
            </p>
          </Section>

          <Section n={9} title="Timeframes">
            <p>
              We aim to review claims within a few business days of receiving your evidence. Once a refund is approved,
              it is issued to your original payment method; your bank or card provider may take several additional
              business days to post it. <em>(Set your exact review and refund service levels before going live.)</em>
            </p>
          </Section>

          <Section n={10} title="Contact us">
            <p>
              Need help with a refund or return? Contact us at{" "}
              <span className="underline" data-testid="refunds-email">kishawhite@yourclassicstyle.onmicrosoft.com</span>. This policy is governed
              by the laws of the State of Georgia, United States.
            </p>
          </Section>

          <p className="mt-12 text-xs text-neutral-400">
            © {new Date().getFullYear()} Your Classic Style. See also our{" "}
            <Link to="/terms" className="underline">Terms &amp; Conditions</Link> and{" "}
            <Link to="/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
